import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Assessment, FAScores, Player } from '../types';
import { Card } from '../styles/GlobalStyles';
import { reportService } from '../services/reportService';
import { LineChart } from './charts/LineChart';
import { RadarChart } from './charts/RadarChart';

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
  max-width: 800px;
  margin: 0 auto;
`;

const ReportHeader = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-xl);
`;

const ReportTitle = styled.h2`
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ReportSubtitle = styled.div`
  color: var(--text-secondary);
  font-size: 1rem;
  margin-top: var(--spacing-sm);
`;

const WelcomeMessage = styled(Card)`
  padding: var(--spacing-xl);
  text-align: center;
  background: linear-gradient(135deg, var(--primary-color)10, var(--success-color)10);
  border: 1px solid var(--primary-color)20;
`;

const WelcomeTitle = styled.h3`
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--primary-color);
`;

const WelcomeText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.6;
`;

const ProgressSummary = styled(Card)`
  padding: var(--spacing-xl);
`;

const SummaryTitle = styled.h3`
  margin: 0 0 var(--spacing-lg) 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
  text-align: center;
`;

const ProgressGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const ProgressItem = styled.div`
  text-align: center;
  padding: var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  background: var(--background-secondary);
`;

const ProgressValue = styled.div<{ color: string }>`
  font-size: 2rem;
  font-weight: 700;
  color: ${props => props.color};
  margin-bottom: var(--spacing-sm);
`;

const ProgressLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 500;
`;

const ProgressDescription = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: var(--spacing-xs);
  line-height: 1.4;
`;

const SkillsBreakdown = styled(Card)`
  padding: var(--spacing-xl);
`;

const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
`;

const SkillCard = styled.div`
  padding: var(--spacing-lg);
  border-radius: var(--border-radius-lg);
  background: var(--background-secondary);
  text-align: center;
`;

const SkillIcon = styled.div<{ color: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 1.25rem;
  margin: 0 auto var(--spacing-md) auto;
`;

const SkillTitle = styled.h4`
  margin: 0 0 var(--spacing-sm) 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const SkillScore = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-sm);
`;

const SkillTrend = styled.div<{ trend: 'up' | 'down' | 'stable' }>`
  font-size: 0.875rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  
  ${props => {
    switch (props.trend) {
      case 'up':
        return 'color: var(--success-color);';
      case 'down':
        return 'color: var(--error-color);';
      default:
        return 'color: var(--text-secondary);';
    }
  }}
`;

const SkillDescription = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: var(--spacing-sm);
  line-height: 1.4;
`;

const EncouragementCard = styled(Card)`
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, var(--success-color)10, var(--primary-color)10);
  border: 1px solid var(--success-color)20;
`;

const EncouragementTitle = styled.h3`
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--success-color);
  text-align: center;
`;

const EncouragementText = styled.p`
  margin: 0;
  color: var(--text-secondary);
  line-height: 1.6;
  text-align: center;
`;

const NextStepsCard = styled(Card)`
  padding: var(--spacing-xl);
`;

const NextStepsList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const ChartContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
`;

const ChartCard = styled(Card)`
  padding: var(--spacing-lg);
  text-align: center;
`;

const ChartTitle = styled.h3`
  margin: 0 0 var(--spacing-md) 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const ChartPlaceholder = styled.div`
  width: 100%;
  height: 250px;
  background: var(--background-secondary);
  border: 2px dashed var(--border-color);
  border-radius: var(--border-radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.875rem;
  text-align: center;
  padding: var(--spacing-md);
`;

const NextStepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--background-secondary);
  border-radius: var(--border-radius-md);
`;

const StepIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--primary-color);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const StepContent = styled.div`
  flex: 1;
`;

const StepTitle = styled.div`
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
`;

const StepDescription = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.4;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-2xl);
  color: var(--text-secondary);
`;

// Skills Development Model configuration with parent-friendly descriptions
const FA_MODEL_PARENT = {
  technical: {
    title: 'Ball Skills',
    color: '#ef4444',
    icon: '⚽',
    description: 'How well your child controls, passes, and shoots the ball'
  },
  physical: {
    title: 'Fitness & Movement',
    color: '#eab308',
    icon: '💪',
    description: 'Your child\'s speed, strength, and coordination on the field'
  },
  psychological: {
    title: 'Mental Strength',
    color: '#10b981',
    icon: '🧠',
    description: 'Confidence, focus, and decision-making during games'
  },
  social: {
    title: 'Teamwork',
    color: '#3b82f6',
    icon: '🤝',
    description: 'How well your child works with teammates and coaches'
  }
};

interface ParentReportViewProps {
  player: Player;
  assessments: Assessment[];
}

export const ParentReportView: React.FC<ParentReportViewProps> = ({
  player,
  assessments
}) => {
  useEffect(() => {
    loadPlayerReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player.id]);

  const loadPlayerReports = async () => {
    try {
      await reportService.getPlayerReports(player.id);
    } catch (error) {
      console.error('Error loading player reports:', error);
    }
  };

  const calculateCornerAverage = (cornerScores: Record<string, number>): number => {
    const values = Object.values(cornerScores);
    return values.reduce((sum, score) => sum + score, 0) / values.length;
  };

  const getSkillTrend = (currentAvg: number, previousAvg?: number): 'up' | 'down' | 'stable' => {
    if (!previousAvg) return 'stable';
    const diff = currentAvg - previousAvg;
    if (diff > 0.5) return 'up';
    if (diff < -0.5) return 'down';
    return 'stable';
  };

  const getTrendText = (trend: 'up' | 'down' | 'stable'): string => {
    switch (trend) {
      case 'up': return '📈 Improving!';
      case 'down': return '📉 Needs focus';
      default: return '➡️ Steady';
    }
  };

  const calculateProgressSummary = () => {
    if (assessments.length < 2) {
      return { improved: 0, declined: 0, unchanged: 24, totalAssessments: assessments.length };
    }

    const latest = assessments[0];
    const previous = assessments[1];
    let improved = 0;
    let declined = 0;
    let unchanged = 0;

    Object.entries(FA_MODEL_PARENT).forEach(([cornerKey]) => {
      const latestScores = latest.scores[cornerKey as keyof FAScores] as Record<string, number>;
      const previousScores = previous.scores[cornerKey as keyof FAScores] as Record<string, number>;

      Object.keys(latestScores).forEach(attr => {
        const change = latestScores[attr] - previousScores[attr];
        if (change > 0) improved++;
        else if (change < 0) declined++;
        else unchanged++;
      });
    });

    return { improved, declined, unchanged, totalAssessments: assessments.length };
  };

  const generateEncouragementMessage = (summary: any, latestAssessment?: Assessment) => {
    if (!latestAssessment) {
      return "We're excited to start tracking your child's football development journey!";
    }

    if (summary.improved > summary.declined) {
      return `Great progress! ${player.firstName} has improved in ${summary.improved} skill areas. Keep up the excellent work!`;
    } else if (summary.improved === summary.declined) {
      return `${player.firstName} is developing steadily. Every player progresses at their own pace, and consistency is key!`;
    } else {
      return `${player.firstName} is working hard on their skills. Remember, development takes time and every training session counts!`;
    }
  };

  const generateNextSteps = (latestAssessment?: Assessment, previousAssessment?: Assessment) => {
    const steps = [
      {
        title: "Keep practicing at home",
        description: "Simple ball control exercises in the garden can make a big difference"
      },
      {
        title: "Encourage regular training",
        description: "Consistent attendance at training sessions helps build skills and confidence"
      },
      {
        title: "Watch football together",
        description: "Watching games can help your child learn tactics and get inspired"
      }
    ];

    if (latestAssessment && previousAssessment) {
      // Find the corner that needs most improvement
      let lowestImprovement = 0;
      let focusArea = '';

      Object.entries(FA_MODEL_PARENT).forEach(([cornerKey, cornerConfig]) => {
        const latestScores = latestAssessment.scores[cornerKey as keyof FAScores] as Record<string, number>;
        const previousScores = previousAssessment.scores[cornerKey as keyof FAScores] as Record<string, number>;
        
        const latestAvg = calculateCornerAverage(latestScores);
        const previousAvg = calculateCornerAverage(previousScores);
        const improvement = latestAvg - previousAvg;

        if (improvement < lowestImprovement) {
          lowestImprovement = improvement;
          focusArea = cornerConfig.title;
        }
      });

      if (focusArea) {
        steps.unshift({
          title: `Focus on ${focusArea}`,
          description: `This area could benefit from extra attention during practice and at home`
        });
      }
    }

    return steps.slice(0, 4); // Return max 4 steps
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generateLineChartData = () => {
    if (assessments.length < 2) return [];

    // Sort assessments by date (oldest first for line chart)
    const sortedAssessments = [...assessments].sort(
      (a, b) => new Date(a.assessmentDate).getTime() - new Date(b.assessmentDate).getTime()
    );

    const lineData = Object.entries(FA_MODEL_PARENT).map(([cornerKey, cornerConfig]) => ({
      label: cornerConfig.title,
      color: cornerConfig.color,
      data: sortedAssessments.map((assessment, index) => {
        const cornerScores = assessment.scores[cornerKey as keyof FAScores] as Record<string, number>;
        const average = calculateCornerAverage(cornerScores);
        return {
          x: index,
          y: average,
          label: cornerConfig.title,
          date: formatDate(assessment.assessmentDate)
        };
      })
    }));

    return lineData;
  };

  const generateRadarChartData = () => {
    if (!latestAssessment) return [];

    return Object.entries(FA_MODEL_PARENT).map(([cornerKey, cornerConfig]) => {
      const cornerScores = latestAssessment.scores[cornerKey as keyof FAScores] as Record<string, number>;
      const average = calculateCornerAverage(cornerScores);
      return {
        label: cornerConfig.title,
        value: average,
        color: cornerConfig.color
      };
    });
  };

  const latestAssessment = assessments[0];
  const previousAssessment = assessments[1];
  const progressSummary = calculateProgressSummary();
  const encouragementMessage = generateEncouragementMessage(progressSummary, latestAssessment);
  const nextSteps = generateNextSteps(latestAssessment, previousAssessment);

  return (
    <ReportContainer>
      <ReportHeader>
        <ReportTitle>{player.firstName}'s Football Development</ReportTitle>
        <ReportSubtitle>
          Progress report for parents • Last updated: {latestAssessment ? formatDate(latestAssessment.assessmentDate) : 'Not yet assessed'}
        </ReportSubtitle>
      </ReportHeader>

      <WelcomeMessage>
        <WelcomeTitle>Welcome to your child's development journey! 🌟</WelcomeTitle>
        <WelcomeText>
          This report shows {player.firstName}'s progress in football skills using a simple, easy-to-understand format. 
          We track four main areas that help young players develop into confident footballers.
        </WelcomeText>
      </WelcomeMessage>

      {assessments.length === 0 ? (
        <EmptyState>
          <h3>Assessment coming soon!</h3>
          <p>Your child's coach will complete the first assessment soon. Check back to see their progress!</p>
        </EmptyState>
      ) : (
        <>
          <ProgressSummary>
            <SummaryTitle>Overall Progress Summary</SummaryTitle>
            <ProgressGrid>
              <ProgressItem>
                <ProgressValue color="var(--success-color)">
                  {progressSummary.improved}
                </ProgressValue>
                <ProgressLabel>Skills Improved</ProgressLabel>
                <ProgressDescription>
                  Areas where {player.firstName} has gotten better
                </ProgressDescription>
              </ProgressItem>
              
              <ProgressItem>
                <ProgressValue color="var(--primary-color)">
                  {progressSummary.totalAssessments}
                </ProgressValue>
                <ProgressLabel>Total Assessments</ProgressLabel>
                <ProgressDescription>
                  Number of times {player.firstName} has been evaluated
                </ProgressDescription>
              </ProgressItem>
              
              <ProgressItem>
                <ProgressValue color="var(--text-secondary)">
                  {progressSummary.unchanged}
                </ProgressValue>
                <ProgressLabel>Skills Maintained</ProgressLabel>
                <ProgressDescription>
                  Areas where {player.firstName} is staying consistent
                </ProgressDescription>
              </ProgressItem>
              
              {progressSummary.declined > 0 && (
                <ProgressItem>
                  <ProgressValue color="var(--warning-color)">
                    {progressSummary.declined}
                  </ProgressValue>
                  <ProgressLabel>Areas to Focus</ProgressLabel>
                  <ProgressDescription>
                    Skills that need a bit more practice
                  </ProgressDescription>
                </ProgressItem>
              )}
            </ProgressGrid>
          </ProgressSummary>

          <ChartContainer>
            <ChartCard>
              <ChartTitle>Progress Over Time</ChartTitle>
              {generateLineChartData().length > 0 ? (
                <LineChart data={generateLineChartData()} />
              ) : (
                <ChartPlaceholder>
                  We'll show {player.firstName}'s progress here after the next assessment!
                </ChartPlaceholder>
              )}
            </ChartCard>
            
            <ChartCard>
              <ChartTitle>Current Skill Levels</ChartTitle>
              {latestAssessment ? (
                <RadarChart data={generateRadarChartData()} />
              ) : (
                <ChartPlaceholder>
                  {player.firstName}'s current skills will appear here after the first assessment
                </ChartPlaceholder>
              )}
            </ChartCard>
          </ChartContainer>

          {latestAssessment && (
            <SkillsBreakdown>
              <SummaryTitle>Current Skill Levels</SummaryTitle>
              <SkillsGrid>
                {Object.entries(FA_MODEL_PARENT).map(([cornerKey, cornerConfig]) => {
                  const currentScores = latestAssessment.scores[cornerKey as keyof FAScores] as Record<string, number>;
                  const previousScores = previousAssessment?.scores[cornerKey as keyof FAScores] as Record<string, number>;
                  
                  const currentAvg = calculateCornerAverage(currentScores);
                  const previousAvg = previousScores ? calculateCornerAverage(previousScores) : undefined;
                  const trend = getSkillTrend(currentAvg, previousAvg);

                  return (
                    <SkillCard key={cornerKey}>
                      <SkillIcon color={cornerConfig.color}>
                        {cornerConfig.icon}
                      </SkillIcon>
                      <SkillTitle>{cornerConfig.title}</SkillTitle>
                      <SkillScore>{currentAvg.toFixed(1)}/10</SkillScore>
                      <SkillTrend trend={trend}>
                        {getTrendText(trend)}
                      </SkillTrend>
                      <SkillDescription>
                        {cornerConfig.description}
                      </SkillDescription>
                    </SkillCard>
                  );
                })}
              </SkillsGrid>
            </SkillsBreakdown>
          )}

          <EncouragementCard>
            <EncouragementTitle>Keep up the great work! 🎉</EncouragementTitle>
            <EncouragementText>
              {encouragementMessage}
            </EncouragementText>
          </EncouragementCard>

          <NextStepsCard>
            <SummaryTitle>How you can help at home</SummaryTitle>
            <NextStepsList>
              {nextSteps.map((step, index) => (
                <NextStepItem key={index}>
                  <StepIcon>{index + 1}</StepIcon>
                  <StepContent>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                  </StepContent>
                </NextStepItem>
              ))}
            </NextStepsList>
          </NextStepsCard>
        </>
      )}
    </ReportContainer>
  );
};